'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, ScrollArea } from '@shared/components/shadcn';
import { ChevronLeft } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepFormProvider } from '../../form-controls/MultistepForm/context/MultistepFormContext';
import { useMultistepFormState } from '../../form-controls/MultistepForm/hooks/useMultistepFormState';
import type { RequiredActionProps } from '../types';
import { type FormValues, getFormSchema } from './common';
import { useReKyc } from './hooks/useReKyc';
import { ConfirmAddressStep } from './steps/ConfirmAddress/ConfirmAddressStep';
import { EditAddressStep } from './steps/EditAddress/EditAddressStep';
import { UpdatedConfirmationStep } from './steps/UpdatedConfirmation/UpdatedConfirmationStep';
import { UpdatingAddressStep } from './steps/UpdatingAddress/UpdatingAddressStep';

function ReKycAlertDialog({ open, onOpenChange }: RequiredActionProps) {
  const { query, confirmMutation, updateMutation } = useReKyc({ enabled: open });

  const t = useTranslations('my_account.rekyc');
  const tCommon = useTranslations('common');

  const form = useForm<FormValues>({
    defaultValues: {
      'edit-address': {
        addressLookup: '',
        address: {
          streetNr: '',
          streetName: '',
          // addressLine1: '',
          addressLine2: '',
          addressLevel2: '',
          postalCode: '',
        },
      },
    },
    mode: 'all',
    resolver: zodResolver(getFormSchema(t)),
  });

  const formState = useMultistepFormState({
    formMethods: form,
    steps: [
      {
        id: 'confirm-address',
        component: <ConfirmAddressStep query={query} confirmMutation={confirmMutation} updateMutation={updateMutation} />,
        dataTrackContainer: 'Confirm Address',
        schema: z.any(),
      },
      {
        id: 'edit-address',
        component: <EditAddressStep />,
        dataTrackContainer: 'Edit Address',
        schema: getFormSchema(t),
      },
      {
        id: 'updating-address',
        component: <UpdatingAddressStep updateMutation={updateMutation} />,
        dataTrackContainer: 'Updating Address',
        schema: z.any(),
      },
      {
        id: 'updated-confirmation',
        component: <UpdatedConfirmationStep />,
        dataTrackContainer: 'Updated Confirmation',
        schema: z.any(),
      },
    ],
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
        // aria-describedby is optional for accessibility, but Radix UI will warn unless we explicitly opt-out.
        // @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/#:~:text=Optionally%2C%20the%20aria%2Ddescribedby%20property
        // @see https://www.radix-ui.com/primitives/docs/components/dialog#description
        aria-describedby={undefined}
        data-track-feature="RE-KYC"
        data-track-container={formState.currentStep?.dataTrackContainer}
      >
        {/* basis-auto is required for the ScrollArea to size itself correctly in Safari */}
        <div className="flex min-h-0 flex-1 flex-col basis-auto">
          <DialogHeader className="relative flex items-center justify-center px-5 border-b border-border">
            {formState.currentStepIndex === 1 && (
              <Button
                variant="secondary"
                onClick={formState.goToPreviousStep}
                className="absolute -mt-2 left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full p-0"
                type="button"
                aria-label={tCommon('back')}
                data-testid="dialog-back-button"
                data-track-name="Back"
              >
                <ChevronLeft size="small" />
              </Button>
            )}
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1 px-6 -mx-6 -mb-6 [&_[data-radix-scroll-area-viewport]_>*]:h-full" type="always">
            <div className="flex flex-col h-full pb-6">
              <FormProvider {...form}>
                <MultistepFormProvider key={`rekyc-step-${formState.currentStepIndex}`} value={formState}>
                  <div className="flex min-h-0 flex-1 flex-col">{formState.currentStep?.component}</div>
                </MultistepFormProvider>
              </FormProvider>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ReKycAlertDialog };
